import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, MessageCircle } from "lucide-react";
import { normalizeClarityOneOtpPhone } from "@shared/customerPhoneIdentity";

type Props = {
  customerId: number;
  enabled: boolean;
  primaryContact?: string | null;
  canViewAsCustomer: boolean;
};

export function ClarityOnePortalToggle({ customerId, enabled, primaryContact, canViewAsCustomer }: Props) {
  const utils = trpc.useUtils();
  const phones = trpc.customers.listPhoneNumbers.useQuery({ customerId });
  const hasPhone = Boolean(normalizeClarityOneOtpPhone(primaryContact)) ||
    (phones.data ?? []).some((phone) => Boolean(normalizeClarityOneOtpPhone(phone.phone)));

  const setEnabled = trpc.customers.setCustomerPortalEnabled.useMutation({
    onSuccess: (data) => {
      toast.success(data.customerPortalEnabled ? "Clarity One portal enabled" : "Clarity One portal disabled");
      void utils.customers.getById.invalidate({ id: customerId });
      void utils.customers.listPhoneNumbers.invalidate({ customerId });
    },
    onError: (err) => toast.error(err.message),
  });

  const viewAsCustomer = trpc.clarityOneAuth.createStaffImpersonation.useMutation({
    onSuccess: (data) => {
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.success(`Opened Clarity One as ${data.customer.name ?? "customer"}`);
    },
    onError: (err) => toast.error(err.message),
  });

  const sendWelcome = trpc.customers.sendClarityOneWelcomeWhatsApp.useMutation({
    onSuccess: (data) => toast.success(data.message),
    onError: (err) => toast.error(err.message),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Clarity One</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="customer-portal-enabled" className="text-sm font-medium">
              Customer portal enabled
            </Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              When enabled, this customer can request WhatsApp login links at one.claritygroup.com.sg.
            </p>
          </div>
          <Switch
            id="customer-portal-enabled"
            checked={enabled}
            disabled={setEnabled.isPending || (!hasPhone && !enabled)}
            onCheckedChange={(checked) => setEnabled.mutate({ customerId, enabled: checked })}
          />
        </div>
        {!phones.isLoading && !hasPhone && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              Add a valid mobile number with country code before enabling Customer Portal login (WhatsApp OTP).
            </p>
        )}
        <div className="pt-1 border-t border-border/60">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            disabled={!enabled || !hasPhone || sendWelcome.isPending}
            onClick={() => sendWelcome.mutate({ customerId })}
          >
            <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
            {sendWelcome.isPending ? "SENDING…" : "RESEND WELCOME TO CUSTOMER"}
          </Button>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Sends the re-initiation message first when required. After the customer replies YES, they receive step-by-step instructions for entering Clarity1.
          </p>
        </div>
        {canViewAsCustomer ? (
          <div className="pt-1 border-t border-border/60">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              disabled={viewAsCustomer.isPending}
              onClick={() => viewAsCustomer.mutate({ customerId })}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View Clarity One as Customer
            </Button>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
              Opens one.claritygroup.com.sg in a new tab for support and testing. This action is logged.
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
